import React, { FC, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { ErrorBoundary, useRouter } from '@funtech-int/rival-react';
import classnames from 'classnames';

import enterFullscreen from '@/scenarios/player/assets/fullscreen.svg';
import { playerRoute } from '@/scenarios/player/routes';

import { usePlayback } from '../../hooks/usePlayback';
import { PlaybackProvider } from '../PlaybackProvider/PlaybackProvider';
import { I18n, useI18n } from '../../hooks/useI18n';
import soundOff from '../../assets/sound-off.svg';
import soundMax from '../../assets/sound-max.svg';
import { useSDKSignal } from '../../hooks/useSDKSignal';
import { isMuted } from '../../lib/predicates';
import { useOnError } from '../../hooks/useOnError';
import { PlayerTracker } from '../PlayerTracker/PlayerTracker';
import { playerBackgroundFirstFrame } from '../../lib/metrics';

import styles from './BackgroundPlayer.module.css';

export interface BackgroundPlayerProps {
    contentId: string;
    buttonSize: 'size60' | 'size52';
    onStart: VoidFunction;
    onFinish: VoidFunction;
}

const getVolumeIcon = (volume: number) => {
    if (isMuted(volume)) return soundOff;
    return soundMax;
};

const getVolumeAriaLabel = (volume: number, i18n: I18n) => {
    if (isMuted(volume)) return i18n('turn_on_sound');
    return i18n('turn_off_sound');
};

const BackgroundPlayerContent: FC<BackgroundPlayerProps> = ({
    contentId,
    buttonSize,
    onFinish,
    onStart,
}) => {
    const i18n = useI18n();
    const playback = usePlayback();
    const router = useRouter();
    const volume = useSDKSignal(playback.volume);
    const onError = useOnError({ contentId });

    const controller = useMemo(
        () => ({
            init: () => {
                const unsubStart = playback.state.listen(() => {
                    if (playback.state.value === 'playing') {
                        onStart();
                    }
                });

                const unsubError = playback.listen('error', (error) => {
                    onError(error, playback.vsid.value);
                });

                const unsubEnd = playback.listen('ended', () => {
                    if (playback.state.value === 'ended') {
                        onFinish();
                    }
                });

                return () => {
                    unsubStart();
                    unsubEnd();
                    unsubError();
                };
            },
            onVolumeClick: () => {
                if (isMuted(playback.volume.value)) {
                    playback.setVolume(1);
                } else {
                    playback.setVolume(0);
                }
            },
            onInViewChange: (inView: boolean) => {
                if (inView) {
                    playback.play();
                } else {
                    playback.pause();
                }
            },
            navigateToPlayer: () => {
                onFinish();
                router.navigate({
                    to: playerRoute({ contentId }, { startTime: playback.currentTime.value }),
                });
            },
        }),
        [playback, onStart, onError, onFinish, router, contentId]
    );

    useEffect(() => controller.init(), [controller]);

    const { ref } = useInView({
        onChange: controller.onInViewChange,
        threshold: 0.2,
    });

    return (
        <div className={styles.root} ref={ref}>
            <div className={styles.backgroundPlayerControls}>
                <button
                    aria-label={getVolumeAriaLabel(volume, i18n)}
                    onClick={controller.onVolumeClick}
                    className={classnames(styles.backgroundPlayerControl, styles[buttonSize])}
                >
                    <img src={getVolumeIcon(volume)} alt="" className={styles.volumeIcon} />
                </button>
                <button
                    aria-label={i18n('aria_label_enter_full_screen')}
                    onClick={controller.navigateToPlayer}
                    className={classnames(styles.backgroundPlayerControl, styles[buttonSize])}
                >
                    <img src={enterFullscreen} alt="" />
                </button>
            </div>
        </div>
    );
};

export const BackgroundPlayer: FC<BackgroundPlayerProps> = (props) => {
    const { contentId } = props;

    return (
        <ErrorBoundary fallback={() => null}>
            <PlaybackProvider
                contentId={contentId}
                streamBlock="trailer"
                fromBlock="movie-screen"
                loader={null}
                objectFit="cover"
                metrics={{ firstFrame: playerBackgroundFirstFrame }}
                muted
            >
                <PlayerTracker contentId={contentId} />
                <BackgroundPlayerContent {...props} />
            </PlaybackProvider>
        </ErrorBoundary>
    );
};
